import { type ReactNode, createContext, useContext } from "react";

import type { ArticleTileDTO } from "~/routes/resources/article-grid";

/**
 * SFCC-11724 — articles-hub SSR seed for the dynamic `ArticleGrid`.
 *
 * The hub's `DynamicGrid` fetches its tile list client-side post-mount, so the
 * default (no-topic) view showed a ~5s skeleton on a cold cache (~8 serial
 * Builder round-trips). The articles route loader now fetches the default list
 * server-side, in parallel with the hub page entry, and provides it here so the
 * default grid paints immediately on first render — no client round-trip for the
 * unfiltered view. A topic-filtered view (`?topic=`) still fetches client-side
 * (the SSR seed is the no-topic list only).
 */
export interface ArticleGridSeed {
  /** SSR-fetched default (no-topic) tile list. */
  items: ArticleTileDTO[];
}

const ArticleGridSeedContext = createContext<ArticleGridSeed | null>(null);

export function ArticleGridSeedProvider({
  seed,
  children,
}: {
  seed: ArticleGridSeed | null;
  children: ReactNode;
}) {
  return (
    <ArticleGridSeedContext.Provider value={seed}>
      {children}
    </ArticleGridSeedContext.Provider>
  );
}

/** SSR seed, or null when none was provided (e.g. the Builder editor canvas). */
// eslint-disable-next-line react-refresh/only-export-components
export function useArticleGridSeed(): ArticleGridSeed | null {
  return useContext(ArticleGridSeedContext);
}
